import React from 'react';
import { SignInPage } from '@backstage/core-components';
import {
  discoveryApiRef,
  fetchApiRef,
  identityApiRef,
  storageApiRef,
} from '@backstage/core-plugin-api';
import {
  ApiBlueprint,
  configApiRef,
  coreExtensionData,
  createExtension,
  createFrontendModule,
  createFrontendPlugin,
  microsoftAuthApiRef,
  type FrontendFeature,
  useApi,
  createExtensionInput,
  appThemeApiRef,
} from '@backstage/frontend-plugin-api';
import { createApp } from '@backstage/frontend-defaults';
import { apiDocsPlugin } from '@backstage/plugin-api-docs';
import { catalogPlugin } from '@backstage/plugin-catalog';
import { catalogImportPlugin } from '@backstage/plugin-catalog-import';
import { orgPlugin } from '@backstage/plugin-org';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { techdocsPlugin } from '@backstage/plugin-techdocs';
import {
  SignInPageBlueprint,
  SignInPageProps,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import authPlugin from '@backstage/plugin-auth';
import { UnifiedThemeProvider } from '@backstage/theme';
import { CssBaseline } from '@material-ui/core';
import catalogPluginAlpha from '@backstage/plugin-catalog/alpha';
import catalogImportPluginAlpha from '@backstage/plugin-catalog-import/alpha';
import scaffolderPluginAlpha from '@backstage/plugin-scaffolder/alpha';
import techdocsPluginAlpha from '@backstage/plugin-techdocs/alpha';
import searchPluginAlpha from '@backstage/plugin-search/alpha';
import apiDocsPluginAlpha from '@backstage/plugin-api-docs/alpha';
import userSettingsPluginAlpha from '@backstage/plugin-user-settings/alpha';
import catalogGraphPluginAlpha from '@backstage/plugin-catalog-graph/alpha';
import homePluginAlpha from '@backstage/plugin-home/alpha';
import { visitsApiRef, VisitsStorageApi } from '@backstage/plugin-home';
import soundcheckPluginAlpha from '@spotify/backstage-plugin-soundcheck/alpha';
import { pandoraTheme } from '@internal/plugin-pandora-react';
import { surveyingPagesModule } from '@internal/backstage-plugin-surveying';
import techdebtFrontendPlugin from '@internal/backstage-plugin-techdebt';

import { RootNav } from './components/Root';
import { heliosCustomPagesPlugin } from './plugins/heliosCustomPagesPlugin';
import { HomePage } from './components/home/HomePage';
import { catalogEntityCompatModule } from './plugins/catalogEntityCompatModule';
import { AppThemeSelector } from '@backstage/core-app-api';

const soundcheckPlugin: FrontendFeature = Object.assign(soundcheckPluginAlpha, {
  info: async () => ({
    packageName: '@spotify/backstage-plugin-soundcheck',
  }),
});

// const techRadarPluginFrontend = createFrontendPlugin({
//   pluginId: 'tech-radar',
// });

// const surveyingPluginFrontend = createFrontendPlugin({
//   pluginId: 'surveying',
// });

const CustomSignInPage = (props: SignInPageProps) => {
  const isDevelopment =
    useApi(configApiRef).getString('auth.environment') === 'development';

  return (
    <SignInPage
      {...props}
      auto
      providers={[
        isDevelopment
          ? 'guest'
          : {
            id: 'microsoft-auth-provider',
            title: 'Pandora',
            message: 'Sign in using your Corporate credentials',
            apiRef: microsoftAuthApiRef,
          },
      ]}
    />
  );
};

const appCustomizations = createFrontendModule({
  pluginId: 'app',
  featureFlags: [
    { name: 'platform-engineering' },
    { name: 'gemcheck' },
    { name: 'new-business-capability-map' },
    { name: 'ai-chat' },
    { name: 'tech-radar' },
    { name: 'techdebt' },
  ],
  extensions: [
    // ThemeBlueprint.make({
    //   name: 'pandora',
    //   params: {
    //     theme: {
    //       id: 'pandora',
    //       title: 'Pandora',
    //       variant: 'light',
    //       Provider: ({ children }) => (
    //         <UnifiedThemeProvider theme={pandoraTheme}>
    //           <CssBaseline />
    //           {children}
    //         </UnifiedThemeProvider>
    //       ),
    //     },
    //   },
    // }),
    ThemeBlueprint.makeWithOverrides({
      name: 'light',
      inputs: {},
      factory: (originalFactory) => {
        return originalFactory(defineParams =>
          defineParams({ theme: {
          id: 'light',
          title: 'Pandora Light',
          variant: 'light',
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={pandoraTheme}>
              <CssBaseline />
              {children}
            </UnifiedThemeProvider>
          ),
        } }))
      }
    }),
    ThemeBlueprint.makeWithOverrides({
      name: 'dark',
      inputs: {},
      factory: (originalFactory) => {
        return originalFactory(defineParams =>
          defineParams({ theme: {
          id: 'dark',
          title: 'Pandora Dark',
          variant: 'dark',
          Provider: ({ children }) => (
            <UnifiedThemeProvider theme={pandoraTheme}>
              <CssBaseline />
              {children}
            </UnifiedThemeProvider>
          ),
        } }))
      }
    }),
    SignInPageBlueprint.make({
      params: {
        loader: async () => CustomSignInPage,
      },
    }),
    createExtension({
      name: 'helios-nav',
      attachTo: { id: 'app/layout', input: 'nav' },
      output: [coreExtensionData.reactElement],
      factory: () => [coreExtensionData.reactElement(<RootNav />)],
    }),
  ],
});

const appApis = createFrontendModule({
  pluginId: 'app',
  extensions: [
    ApiBlueprint.make({
      name: 'home-visits-api',
      params: defineParams =>
        defineParams({
          api: visitsApiRef,
          deps: { storageApi: storageApiRef, identityApi: identityApiRef },
          factory: ({ storageApi, identityApi }) =>
            VisitsStorageApi.create({ storageApi, identityApi }),
        }),
    }),
  ],
});

// const techRadarApis = createFrontendModule({
//   pluginId: 'tech-radar',
//   extensions: [
//     ApiBlueprint.make({
//       name: 'tech-radar',
//       params: defineParams =>
//         defineParams({
//           api: techRadarApiRef,
//           deps: { discoveryApi: discoveryApiRef, fetchApi: fetchApiRef },
//           factory: ({ discoveryApi, fetchApi }) =>
//             new TechRadarClient({ discoveryApi, fetchApi }),
//         }),
//     }),
//     ApiBlueprint.make({
//       name: 'community-tech-radar',
//       params: defineParams =>
//         defineParams({
//           api: communityTechRadarApiRef,
//           deps: {
//             discoveryApi: discoveryApiRef,
//             fetchApi: fetchApiRef,
//             identityApi: identityApiRef,
//           },
//           factory: ({ discoveryApi, fetchApi, identityApi }) =>
//             new TechRadarRadarClient({ discoveryApi, fetchApi, identityApi }),
//         }),
//     }),
//   ],
// });

const app = createApp({
  features: [
    // authPlugin,
    catalogPluginAlpha,
    // catalogImportPluginAlpha,
    // scaffolderPluginAlpha,
    // techdocsPluginAlpha,
    // searchPluginAlpha,
    // apiDocsPluginAlpha,
    // userSettingsPluginAlpha,
    // catalogGraphPluginAlpha,
    // homePluginAlpha,
    // soundcheckPlugin,
    // eaPackPagesModule,
    // techRadarPluginFrontend,
    // surveyingPluginFrontend,
    // surveyingPagesModule,
    // techRadarPagesModule,
    heliosCustomPagesPlugin,
    catalogEntityCompatModule,
    techdebtFrontendPlugin,
    appApis,
    // techRadarApis,
    appCustomizations,
  ],
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
});

const appRoot = app.createRoot();

const App = () => appRoot;

export default App;
